/* ── The movement ─────────────────────────────────────────────────────────
   A three.js clockwork train living behind the hero dial, like the open
   back of a skeleton watch. Brass wheels turn at gear-true ratios, the
   escape wheel ticks once per second in step with the SVG dial, the
   balance wheel breathes, and scrolling the page advances the train.
   No glow, no bloom — just warm light on dark metal. */

import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { useEffect, useRef } from 'react';

const INK = 0x0b0d12;

/* A gear silhouette: trapezoid teeth on a rim, hollowed to `holeR`. */
function gearGeometry(teeth, module, thickness, holeR) {
  const pitchR = (teeth * module) / 2;
  const outer = pitchR + module * 0.95;
  const root = pitchR - module;
  const shape = new THREE.Shape();
  const profile = [[0, root], [0.3, root], [0.38, outer], [0.62, outer], [0.7, root]];
  for (let i = 0; i < teeth; i++) {
    for (const [f, rad] of profile) {
      const a = ((i + f) / teeth) * Math.PI * 2;
      const x = Math.cos(a) * rad;
      const y = Math.sin(a) * rad;
      if (i === 0 && f === 0) shape.moveTo(x, y);
      else shape.lineTo(x, y);
    }
  }
  shape.closePath();
  const hole = new THREE.Path();
  hole.absarc(0, 0, holeR, 0, Math.PI * 2, true);
  shape.holes.push(hole);
  return new THREE.ExtrudeGeometry(shape, {
    depth: thickness,
    bevelEnabled: true,
    bevelThickness: 1.4,
    bevelSize: 1.4,
    bevelSegments: 2,
    curveSegments: 4,
  });
}

/* A full wheel: toothed rim, hub, and spokes. */
function makeWheel({ teeth, module = 9, thickness = 9, material, spokes = 4 }) {
  const group = new THREE.Group();
  const pitchR = (teeth * module) / 2;
  const rimHole = pitchR * 0.7;

  const gear = new THREE.Mesh(gearGeometry(teeth, module, thickness, rimHole), material);
  gear.position.z = -thickness / 2;
  group.add(gear);

  const hub = new THREE.Mesh(
    new THREE.CylinderGeometry(Math.max(6, pitchR * 0.14), Math.max(6, pitchR * 0.14), thickness * 1.7, 20),
    material
  );
  hub.rotation.x = Math.PI / 2;
  group.add(hub);

  for (let i = 0; i < spokes; i++) {
    const a = (i / spokes) * Math.PI * 2;
    const spoke = new THREE.Mesh(
      new THREE.BoxGeometry(rimHole + 6, Math.max(4.5, pitchR * 0.075), thickness * 0.65),
      material
    );
    spoke.position.set((Math.cos(a) * rimHole) / 2, (Math.sin(a) * rimHole) / 2, 0);
    spoke.rotation.z = a;
    group.add(spoke);
  }
  return group;
}

/* The balance wheel: a weighted ring that swings back and forth. */
function makeBalance(radius, material) {
  const group = new THREE.Group();
  const ring = new THREE.Mesh(new THREE.TorusGeometry(radius, radius * 0.09, 10, 48), material);
  group.add(ring);
  for (let i = 0; i < 2; i++) {
    const arm = new THREE.Mesh(
      new THREE.BoxGeometry(radius * 2, radius * 0.09, radius * 0.09),
      material
    );
    arm.rotation.z = (i / 2) * Math.PI;
    group.add(arm);
  }
  const hub = new THREE.Mesh(new THREE.CylinderGeometry(radius * 0.12, radius * 0.12, radius * 0.24, 16), material);
  hub.rotation.x = Math.PI / 2;
  group.add(hub);
  return group;
}

/* Phase constant so a child wheel's teeth interleave with its parent's. */
function meshChild(parentTeeth, childTeeth, contactAngle) {
  const ratio = parentTeeth / childTeeth;
  const k = contactAngle * (1 + ratio) + Math.PI / childTeeth;
  return (parentRot) => -ratio * parentRot + k;
}

export default function ClockMovement({ reduced, variant = 'hero' }) {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || typeof WebGLRenderingContext === 'undefined') return undefined;

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'low-power' });
    } catch (e) {
      return undefined; // no WebGL — the page simply keeps its 2D hero
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 0.95;
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    scene.fog = new THREE.Fog(INK, 1, 3000); // ranges set in layout(), relative to camera depth

    const camera = new THREE.PerspectiveCamera(40, 1, 1, 3000);

    /* A soft studio environment so the metals actually read as metal */
    const pmrem = new THREE.PMREMGenerator(renderer);
    const envTexture = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    scene.environment = envTexture;

    /* Materials — dark horological metals, lit sparingly */
    const brass = new THREE.MeshStandardMaterial({ color: 0x63512f, metalness: 0.85, roughness: 0.44, envMapIntensity: 0.2 });
    const bronze = new THREE.MeshStandardMaterial({ color: 0x4c422c, metalness: 0.82, roughness: 0.5, envMapIntensity: 0.17 });
    const steel = new THREE.MeshStandardMaterial({ color: 0x454c5c, metalness: 0.78, roughness: 0.48, envMapIntensity: 0.14 });
    const shadowMetal = new THREE.MeshStandardMaterial({ color: 0x141821, metalness: 0.6, roughness: 0.65, envMapIntensity: 0.07 });

    /* Lights — warm key, cool rim, no glow */
    scene.add(new THREE.HemisphereLight(0x343b4a, INK, 0.3));
    const key = new THREE.DirectionalLight(0xffdfb0, 0.6);
    key.position.set(-320, 400, 540);
    scene.add(key);
    const rim = new THREE.DirectionalLight(0x8797bd, 0.35);
    rim.position.set(460, -180, -260);
    scene.add(rim);
    const warm = new THREE.PointLight(0xb9a079, 0.35, 1400, 1.6);
    warm.position.set(140, 90, 300);
    scene.add(warm);

    /* The train, tilted so the depth reads */
    const root = new THREE.Group();
    root.rotation.x = -0.34;
    root.rotation.y = -0.22;
    scene.add(root);

    const T1 = 34, T2 = 16, T3 = 22, M = 9;
    const r = (t) => (t * M) / 2;

    const wheel1 = makeWheel({ teeth: T1, module: M, thickness: 10, material: brass, spokes: 5 });
    root.add(wheel1);

    const phi2 = -0.65;
    const wheel2 = makeWheel({ teeth: T2, module: M, thickness: 9, material: bronze, spokes: 3 });
    wheel2.position.set(Math.cos(phi2) * (r(T1) + r(T2)), Math.sin(phi2) * (r(T1) + r(T2)), -14);
    root.add(wheel2);
    const rot2 = meshChild(T1, T2, phi2);

    const phi3 = 2.25;
    const wheel3 = makeWheel({ teeth: T3, module: M, thickness: 9, material: steel, spokes: 4 });
    wheel3.position.set(Math.cos(phi3) * (r(T1) + r(T3)), Math.sin(phi3) * (r(T1) + r(T3)), 12);
    root.add(wheel3);
    const rot3 = meshChild(T1, T3, phi3);

    const ESC_T = 12;
    const escape = makeWheel({ teeth: ESC_T, module: 8, thickness: 8, material: brass, spokes: 3 });
    escape.position.set(-236, -168, 46);
    root.add(escape);

    const balance = makeBalance(58, bronze);
    balance.position.set(-296, -36, 70);
    root.add(balance);

    /* A deep background wheel, almost ink — texture, not subject */
    const ghost = makeWheel({ teeth: 28, module: 10, thickness: 8, material: shadowMetal, spokes: 4 });
    ghost.position.set(0, 0, -230);
    scene.add(ghost);

    /* Layout responds to the hero's size */
    let W = 1, H = 1;
    const layout = () => {
      W = Math.max(1, mount.clientWidth);
      H = Math.max(1, mount.clientHeight);
      renderer.setSize(W, H);
      camera.aspect = W / H;
      camera.position.z = H / 2 / Math.tan((camera.fov * Math.PI) / 360);
      camera.updateProjectionMatrix();
      // depth fade starts just behind the train and swallows the far plane
      scene.fog.near = camera.position.z + 80;
      scene.fog.far = camera.position.z + 520;
      const compact = W < 960;
      if (variant === 'cta') {
        // wheels peek out from behind the brass panel's edges
        root.position.set(W * 0.42, H * 0.1, -30);
        root.scale.setScalar(0.62);
        ghost.visible = true;
        ghost.position.set(-W * 0.44, H * 0.04, -150);
      } else {
        // anchor the train to the dial: clock-wrap is min(76vh, 700px) wide, right: -6vw
        const dialCx = W / 2 - (Math.min(0.76 * H, 700) / 2 - 0.06 * W);
        root.position.set(compact ? 0 : dialCx, compact ? H * 0.06 : -8, 0);
        root.scale.setScalar(compact ? 0.78 : 1);
        ghost.visible = !compact;
        ghost.position.set(-W * 0.43, -H * 0.46, -260);
      }
    };
    layout();
    const ro = new ResizeObserver(layout);
    ro.observe(mount);

    /* Pointer parallax (fine pointers only) */
    let px = 0, py = 0;
    const finePointer = window.matchMedia('(pointer: fine)').matches;
    const onPointer = (e) => {
      px = (e.clientX / window.innerWidth) * 2 - 1;
      py = (e.clientY / window.innerHeight) * 2 - 1;
    };
    if (finePointer && !reduced) window.addEventListener('pointermove', onPointer, { passive: true });

    /* Only animate while the hero is on screen */
    let visible = true;
    const io = new IntersectionObserver(([e]) => {
      visible = e.isIntersecting;
      if (!reduced) renderer.setAnimationLoop(visible ? animate : null);
    });
    io.observe(mount);

    const clock = new THREE.Clock();
    let extra = 0;
    let escRot = 0, escTarget = 0, lastSec = Math.floor(Date.now() / 1000);

    function pose(elapsed) {
      const base = elapsed * 0.055 + extra;
      wheel1.rotation.z = base;
      wheel2.rotation.z = rot2(base);
      wheel3.rotation.z = rot3(base);
      escape.rotation.z = escRot;
      balance.rotation.z = Math.sin(elapsed * Math.PI) * 0.55;
      ghost.rotation.z = -elapsed * 0.02;
    }

    function animate() {
      const dt = Math.min(clock.getDelta(), 0.1);
      const elapsed = clock.elapsedTime;

      // scroll advances the train
      const extraTarget = (window.scrollY || 0) * 0.0022;
      extra += (extraTarget - extra) * Math.min(1, dt * 4);

      // the escapement ticks once per second, in step with the dial
      const sec = Math.floor(Date.now() / 1000);
      if (sec !== lastSec) {
        lastSec = sec;
        escTarget += (Math.PI * 2) / ESC_T;
      }
      escRot += (escTarget - escRot) * Math.min(1, dt * 13);

      pose(elapsed);

      camera.position.x += (px * 26 - camera.position.x) * Math.min(1, dt * 3);
      camera.position.y += (-py * 20 - camera.position.y) * Math.min(1, dt * 3);
      camera.lookAt(0, 0, 0);

      renderer.render(scene, camera);
    }

    if (reduced) {
      // one honest still of the movement, mid-tick
      escRot = escTarget = (Math.PI * 2) / ESC_T / 2;
      pose(2.6);
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
    } else {
      renderer.setAnimationLoop(animate);
    }

    return () => {
      renderer.setAnimationLoop(null);
      io.disconnect();
      ro.disconnect();
      if (finePointer && !reduced) window.removeEventListener('pointermove', onPointer);
      scene.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();
      });
      [brass, bronze, steel, shadowMetal].forEach((m) => m.dispose());
      envTexture.dispose();
      pmrem.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, [reduced, variant]);

  return <div ref={mountRef} className={`movement-canvas ${variant}-canvas`} aria-hidden="true" />;
}
